import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

interface Message {
  id: number;
  sender: "visitor" | "operator";
  text: string;
  timestamp: string;
}

const VID_KEY = "smt_chat_vid";

function getVisitorId(): string {
  let vid = localStorage.getItem(VID_KEY);
  if (!vid) {
    vid = typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
    localStorage.setItem(VID_KEY, vid);
  }
  return vid;
}

export function ChatWidget() {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [unread, setUnread] = useState(0);
  const [wsKey, setWsKey] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const isOpenRef = useRef(false);
  const vidRef = useRef(getVisitorId());
  const historyRef = useRef<string[]>([]);
  const startRef = useRef(new Date().toISOString());
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Track page navigation
  useEffect(() => {
    const h = historyRef.current;
    if (h[h.length - 1] !== location.pathname) h.push(location.pathname);
    if (h.length > 20) h.shift();
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "context",
          page: location.pathname,
          history: historyRef.current,
          sessionStart: startRef.current,
        })
      );
    }
  }, [location.pathname]);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input on open (unread is cleared in the open handler, not an effect)
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // WebSocket connection — runs when panel opens or on reconnect
  useEffect(() => {
    if (!isOpen) return;

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/api/chat/ws?vid=${vidRef.current}`;
    const ws = new WebSocket(url);
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    ws.onopen = () => {
      setConnected(true);
      ws.send(
        JSON.stringify({
          type: "context",
          // window.location, not the router hook — the socket must NOT
          // reconnect on every navigation (page updates flow via the
          // context effect above)
          page: window.location.pathname,
          history: historyRef.current,
          sessionStart: startRef.current,
        })
      );
    };

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "connected" && msg.messages?.length) {
          setMessages(
            msg.messages.map((m: any) => ({
              id: m.id,
              sender: m.sender,
              text: m.message,
              timestamp: m.created_at,
            }))
          );
        } else if (msg.type === "message") {
          setMessages((prev) => [
            ...prev,
            { id: msg.id, sender: "operator", text: msg.text, timestamp: msg.timestamp },
          ]);
          if (!isOpenRef.current) setUnread((n) => n + 1);
        }
      } catch {
        /* ignore parse errors */
      }
    };

    ws.onclose = () => {
      setConnected(false);
      reconnectTimer = setTimeout(() => {
        if (isOpenRef.current) setWsKey((k) => k + 1);
      }, 3000);
    };

    ws.onerror = () => ws.close();
    wsRef.current = ws;

    return () => {
      ws.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [isOpen, wsKey, user?.userId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => wsRef.current?.close();
  }, []);

  if (isLoading) return null;

  const sendMessage = () => {
    const text = input.trim();
    if (!text || wsRef.current?.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "message", text }));
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), sender: "visitor", text, timestamp: new Date().toISOString() },
    ]);
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div
          style={{
            position: "fixed",
            bottom: "5.5rem",
            right: "1rem",
            width: "22rem",
            maxWidth: "calc(100vw - 2rem)",
            height: "28rem",
            maxHeight: "calc(100vh - 7rem)",
            zIndex: 9998,
            display: "flex",
            flexDirection: "column",
            borderRadius: "1rem",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "#1a1b2e",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #b87800, #d4920a)",
              padding: "0.75rem 1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: connected ? "#4ade80" : "#fbbf24",
                  boxShadow: connected ? "0 0 6px #4ade80" : "none",
                }}
              />
              <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>Live Chat</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: "none",
                border: "none",
                color: "rgba(255,255,255,0.85)",
                cursor: "pointer",
                fontSize: "1.35rem",
                lineHeight: 1,
                padding: "0 0.25rem",
              }}
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "0.75rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
            }}
          >
            {messages.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  color: "#8c8fa1",
                  fontSize: "0.8rem",
                  marginTop: "3rem",
                  lineHeight: 1.6,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    background: "rgba(184,120,0,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 0.75rem",
                  }}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#b87800"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <p style={{ fontWeight: 500, color: "#c5c7d4", marginBottom: 4 }}>
                  Need help? Chat with us!
                </p>
                <p>Type a message below to get started.</p>
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  alignSelf: msg.sender === "visitor" ? "flex-end" : "flex-start",
                  maxWidth: "80%",
                }}
              >
                <div
                  style={{
                    padding: "0.5rem 0.75rem",
                    borderRadius:
                      msg.sender === "visitor"
                        ? "0.75rem 0.75rem 0.25rem 0.75rem"
                        : "0.75rem 0.75rem 0.75rem 0.25rem",
                    background: msg.sender === "visitor" ? "#b87800" : "#2a2b3e",
                    color: "#fff",
                    fontSize: "0.85rem",
                    lineHeight: 1.45,
                    wordBreak: "break-word",
                  }}
                >
                  {msg.text}
                </div>
                <div
                  style={{
                    fontSize: "0.65rem",
                    color: "#6b6d80",
                    marginTop: 2,
                    textAlign: msg.sender === "visitor" ? "right" : "left",
                    paddingInline: 4,
                  }}
                >
                  {new Date(msg.timestamp).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div
            style={{
              padding: "0.5rem 0.75rem",
              borderTop: "1px solid rgba(255,255,255,0.06)",
              background: "#22233a",
              display: "flex",
              gap: "0.5rem",
              alignItems: "flex-end",
              flexShrink: 0,
            }}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                color: "#fafafc",
                fontSize: "0.85rem",
                resize: "none",
                maxHeight: "4rem",
                fontFamily: "inherit",
                padding: "0.4rem 0",
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim()}
              style={{
                background: input.trim() ? "#b87800" : "#3a3b4e",
                border: "none",
                borderRadius: "0.5rem",
                padding: "0.4rem 0.75rem",
                color: input.trim() ? "#fff" : "#6b6d80",
                cursor: input.trim() ? "pointer" : "default",
                fontSize: "0.85rem",
                fontWeight: 500,
                transition: "background 0.15s, color 0.15s",
                flexShrink: 0,
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {/* Chat Bubble + Label wrapper */}
      <div
        onClick={() => {
          if (!isOpen) setUnread(0);
          setIsOpen(!isOpen);
        }}
        role="button"
        aria-label={isOpen ? "Close chat" : "Open live chat"}
        style={{
          position: "fixed",
          bottom: "1rem",
          right: "1rem",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.4rem",
          cursor: "pointer",
          transition: "transform 0.15s",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.transform = "scale(1.08)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.transform = "scale(1)";
        }}
      >
        {/* LIVE CHAT label */}
        {!isOpen && (
          <div
            style={{
              background: "linear-gradient(135deg, #b87800, #d4920a)",
              color: "#fff",
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.05em",
              padding: "0.3rem 0.75rem",
              borderRadius: "1rem",
              boxShadow: "0 2px 8px rgba(184, 120, 0, 0.35)",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
          >
            LIVE CHAT
          </div>
        )}

        {/* Circle icon */}
        <div
          style={{
            width: "3.5rem",
            height: "3.5rem",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #b87800, #d4920a)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 16px rgba(184, 120, 0, 0.4)",
            position: "relative",
          }}
        >
        {isOpen ? (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
        {!!unread && !isOpen && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              background: "#ef4444",
              color: "#fff",
              borderRadius: "50%",
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.7rem",
              fontWeight: 700,
              border: "2px solid #1a1b2e",
            }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        </div>
      </div>
    </>
  );
}
